import { useEffect, useState } from 'react'
import { AlertTriangle, Coins } from 'lucide-react'
import { getInviteCost, type InviteCost } from '@/api/billing'

/**
 * What adding this person will cost, shown BEFORE the invite is attempted.
 *
 * The reactive 402 is still handled (a race is always possible), but a cost the
 * user only discovers by failing is a bad experience — and a manager who
 * doesn't know a seat costs money has no reason to keep the wallet funded.
 *
 * `onAffordability` lets the dialog disable its submit button while the wallet
 * cannot cover the seat.
 */
export default function InviteCostNotice({
  projectId, email, onAffordability,
}: {
  projectId: string
  email: string
  onAffordability?: (canAfford: boolean) => void
}) {
  const [cost, setCost] = useState<InviteCost | null>(null)

  useEffect(() => {
    let cancelled = false
    // Debounced: this fires as the manager types an email address.
    const timer = setTimeout(() => {
      getInviteCost(projectId, email.trim() || undefined)
        .then((data) => {
          if (cancelled) return
          setCost(data)
          onAffordability?.(data.amount_minor <= 0 || data.sufficient)
        })
        .catch(() => {
          if (cancelled) return
          // Billing unavailable must never block an invite.
          setCost(null)
          onAffordability?.(true)
        })
    }, 350)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [projectId, email, onAffordability])

  if (!cost || cost.amount_minor <= 0) {
    if (cost?.already_charged) {
      return (
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          This person has already been paid for on this application — re-inviting is free.
        </div>
      )
    }
    return null
  }

  const short = !cost.sufficient
  return (
    <div
      style={{
        display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px',
        borderRadius: 6, fontSize: 13,
        background: 'var(--color-bg-secondary)',
        border: `1px solid ${short ? 'var(--color-danger, #d64545)' : 'var(--color-border)'}`,
      }}
    >
      {short
        ? <AlertTriangle size={14} style={{ color: 'var(--color-danger, #d64545)', flexShrink: 0, marginTop: 1 }} />
        : <Coins size={14} style={{ color: 'var(--color-accent)', flexShrink: 0, marginTop: 1 }} />}
      <div>
        Adding this user costs <strong>{cost.display}</strong>
        {cost.invitee_is_pro && ' (Pro rate)'} from this application's wallet.
        <div style={{ color: 'var(--color-text-secondary)', marginTop: 2 }}>
          Wallet balance: {cost.balance_display}
          {short && ' — add funds before inviting.'}
        </div>
      </div>
    </div>
  )
}
